import ganttChartService from "../../../service/ganttChartService";
import { useAppSelector } from "../../../store/hooks";
import { Employee } from "../../../types/ganttChart";
import { ganttChartVerticalValues } from "../../../utils/ganttChartValues";
import VerticalOperationalTask from "./VerticalOperationalTasks";
import VerticalGanttChartTransportationTask from "./VerticalGanttChartTransportationTask";
import VerticalWorkTask from "./VerticalWorkTask";
import UnplannedTaskChartPositionAllocationService from "../../../service/UnplannedTaskChartPositionAllocationService";
import DragAndDropTask from "../dragAndDrop/DragAndDropTask";

const VerticalGanttChartTaskWrapper = ({
  xPosition,
  employee,
  useColorGroupingsAsTeamSplitter,
}: {
  xPosition: number;
  employee: Employee;
  useColorGroupingsAsTeamSplitter: boolean;
}) => {
  const { verticalColumnWidth } = useAppSelector(
    (state) => state.ganttChartSlice,
  );
  const { timelineRange, pixelPerMinute } = useAppSelector(
    (state) => state.timelineSlice,
  );
  const { isDragging, xPosition: x } = useAppSelector(
    (state) => state.dragAndDropSlice,
  );

  const getYPosition = (startTime: string) => {
    return ganttChartVerticalValues.findTaskYPosition(
      timelineRange!,
      pixelPerMinute,
      startTime,
    );
  };

  const getHeight = (startTime: string, endTime: string) => {
    return (
      ganttChartService.timeDifferenceInMinutes(startTime, endTime) *
      pixelPerMinute
    );
  };

  /* Cluster tasks and set ganttChartIndex for each */
  const overlappingClustersOfOperationalTasks =
    UnplannedTaskChartPositionAllocationService.clusterOverlappingTasks(
      employee.tasks.operationalTasks,
      (task) => new Date(task.start),
      (task) => new Date(task.end),
    );

  /* Sort tasks according to ganttChartIndex, as svg files are drawn in an order.
   This ensures that smaller tasks are shown on top. Cannot be handled with css on svg elements. */
  const sortedArrayOfClusteredOperationalTasks =
    overlappingClustersOfOperationalTasks.sort(
      (a, b) => a.ganttChartPosition - b.ganttChartPosition,
    );

  return (
    <>
      <>
        {employee.tasks.workTasks.map((task, index) => (
          <VerticalWorkTask
            employee={employee}
            key={"nexus-worktask" + index}
            x={xPosition}
            task={task}
            y={getYPosition(task.start)}
            width={ganttChartVerticalValues.getTaskWidth(verticalColumnWidth)}
            height={getHeight(task.start, task.end)}
            useColorGroupingsAsTeamSplitter={useColorGroupingsAsTeamSplitter}
          />
        ))}

        {sortedArrayOfClusteredOperationalTasks.map((task, index) => (
          <VerticalOperationalTask
            employee={employee}
            key={"nexus-operationaltask" + index}
            xPosition={xPosition}
            task={task}
            yPosition={getYPosition(task.start)}
            width={ganttChartVerticalValues.getTaskWidth(verticalColumnWidth)}
            height={getHeight(task.start, task.end)}
          />
        ))}

        {employee.tasks.transportationTasks.map((task, index) => (
          <VerticalGanttChartTransportationTask
            employee={employee}
            key={"nexus-transportationtask" + index}
            xPosition={xPosition}
            task={task}
            yPosition={getYPosition(task.start)}
            transportationType={employee.transportationType}
            width={ganttChartVerticalValues.getTaskWidth(verticalColumnWidth)}
          />
        ))}

        {employee.shiftSchedule.lockedForPlanning && (
          <rect
            x={xPosition}
            y={0}
            width={ganttChartVerticalValues.getOverlayWidth(
              verticalColumnWidth,
            )}
            height="100%"
            fill="var(--col-graph-first-1)"
            opacity="0.2"
            style={{ pointerEvents: "none" }}
          />
        )}
        {/* The task being dragged, when in drag and drop mode. */}
        {isDragging && <DragAndDropTask />}
      </>
    </>
  );
};

export default VerticalGanttChartTaskWrapper;
